/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Checkbox, Divider, IconButton, List, ListItem, Typography} from '@mui/material';
import ArgosFlexbox from '../../../../../../../components/Containers/ArgosFlexbox';
import _ from 'lodash';
import EditPropertyDialog from '../../../../../../../components/Dialogs/RedlineAppDialogs/EditPropertyDialog';
import {ArgosAddIcon, ArgosDeleteAllIcon, ArgosDeleteIcon} from '../../../../../../../components/Icons/ArgosActionIcons';
import {useState} from 'react';
import {OBJECT_TYPE_STR, ROAD_TYPE_TYPE_STR} from '../../RightPanelComponents/constants';
import AddPropertyDialog from '../../../../../../../components/Dialogs/RedlineAppDialogs/AddPropertyDialog';

interface Props {
  configState: any;
  setConfigState: Function;
  name: string,
  property: {
    type: string; // Driving Scenario Property, ex: "Road Type"
  };
  propertySelected: Function; // Mark this property to include in the bulk edit request
}

/**
 * Renders property edit field for bulk editing depending on given property type.
 *
 * @param property - The property type (ex: Object, Road Type...etc)
 */
const BulkEditField: React.FC<Props> = (props) => {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const handleCheck = () => {
    setIsChecked(!isChecked);
    props.propertySelected(props.name);
  }

  /**
   * Objects or Road Type Property Type - can add multiple within one bulk edit.
   */
  const [listItemsList, setListItemsList] = useState<{ objectId: string, title: string }[]>([]); // Objects: (Category - Name) to show in list
  const [isShowingAddListItemWindow, setIsShowingAddListItem] = useState<boolean>(false);
  const addListItems = (selectedItems: []) => {
    var newConfig = props.configState;
    newConfig[props.property.type] = _.union(newConfig[props.property.type], selectedItems);
    props.setConfigState(newConfig);

    var newItemsList = listItemsList
    selectedItems.forEach((object: any) => {
      if (object.type === ROAD_TYPE_TYPE_STR) {
        newItemsList.push({
          title: object.name,
          objectId: object.name
        })
      } else if (object.type === OBJECT_TYPE_STR) {
        newItemsList.push({
          objectId: object.objectId,
          title: object.category + " - " + object.name
        })
      }
    })
    setListItemsList(newItemsList)
  };
  const deleteListItem = (index: number) => {
    var newConfig = props.configState;
    if (props.property.type === ROAD_TYPE_TYPE_STR) { // Road Types don't have IDs
      newConfig[props.property.type] = newConfig[props.property.type].filter((item: any) => item.name !== listItemsList[index].objectId);
    } else if (props.property.type === OBJECT_TYPE_STR) { // Objects have objectIds to differentiate between same named objects
      newConfig[props.property.type] = newConfig[props.property.type].filter((item: any) => item.objectId !== listItemsList[index].objectId);
    }
    props.setConfigState(newConfig);
    setListItemsList(listItemsList.filter((_, i) => i !== index)); // Remove from list visually
  };
  const deleteAllListItems = () => {
    var newConfig = props.configState;
    newConfig[props.property.type] = [];
    props.setConfigState(newConfig);
    setListItemsList([]);
  }

  const addPropertiesWindow = (
    <ArgosFlexbox style={{alignItems: "center", justifyContent: "space-between", marginTop: "10px"}}>
      <Typography>{"Add " + (props.property.type === OBJECT_TYPE_STR ? OBJECT_TYPE_STR + "(s)" : ROAD_TYPE_TYPE_STR + "(s)")}</Typography>
      <AddPropertyDialog
        onClose={() => setIsShowingAddListItem(false)}
        multipleSelect={props.property.type === ROAD_TYPE_TYPE_STR}
        propertyType={props.property.type}
        configState={props.configState}
        setBulkConfig={addListItems}
      />
    </ArgosFlexbox>
  );

  const itemsListUI = (<ArgosFlexbox style={{flexDirection: "column", marginTop: "10px"}}>
    <ArgosFlexbox key={props.property.type} style={{alignItems: "center", justifyContent: "space-between"}}>
      <ArgosFlexbox style={{alignItems: "center"}}><Checkbox checked={isChecked} onChange={handleCheck}/>
        <Typography>{props.property.type === OBJECT_TYPE_STR ? OBJECT_TYPE_STR + "s" : ROAD_TYPE_TYPE_STR}</Typography>
      </ArgosFlexbox>
      <ArgosFlexbox>
        <IconButton hidden={Object.keys(listItemsList).length < 1} aria-label="delete all items" onClick={() => {
          deleteAllListItems()
        }}>
          <ArgosDeleteAllIcon/>
        </IconButton>
        <IconButton aria-label="add" onClick={() => {
          setIsShowingAddListItem(true)
        }}>
          <ArgosAddIcon/>
        </IconButton>
      </ArgosFlexbox>
    </ArgosFlexbox>
    <ArgosFlexbox style={{maxHeight: "250px", overflow: "scroll", overflowY: "auto", overflowX: "hidden"}}>
      {Object.keys(listItemsList).length > 0 &&
        <List sx={{listStyleType: 'disc', marginBottom: "0px"}} dense={true}>
          {listItemsList.map((item, index) => (
            <ListItem sx={{marginLeft: "20px", display: 'list-item'}} key={index}>
              {item.title}
              <IconButton edge="end" aria-label="add" onClick={() => deleteListItem(index)}>
                <ArgosDeleteIcon/>
              </IconButton>
            </ListItem>
          ))}
        </List>
      }
    </ArgosFlexbox>
    <Divider style={{background: 'black'}}/>
  </ArgosFlexbox>);

  if (props.property.type === OBJECT_TYPE_STR || props.property.type === ROAD_TYPE_TYPE_STR) {
    return isShowingAddListItemWindow ? addPropertiesWindow : itemsListUI
  }

  return (
    <ArgosFlexbox style={{flexDirection: "column", marginBottom: "5px"}}>
      <ArgosFlexbox key={props.property.type} style={{alignItems: "center", justifyContent: "space-between"}}>
        <div style={{alignItems: "center"}}><Checkbox checked={isChecked} onChange={handleCheck}/> {props.property.type}</div>
        <EditPropertyDialog
          key={props.property.type}
          hideSaveAndCancel={true}
          propertyType={props.property.type}
          data={[]} onSave={undefined} onClose={undefined}
          setBulkConfig={props.setConfigState}
        />
      </ArgosFlexbox>
      <Divider style={{background: 'black'}}/>
    </ArgosFlexbox>
  );
}

export default BulkEditField;